/**
 * Tela das manutenções da Fazenda Boa Vista, com o miolo da origem.
 *
 * Papel: abre com o cabeçalho do módulo — "Residências", com o subtítulo
 * `FAZENDA BOA VISTA | MANUTENÇÕES` e o "← Voltar" para `/residencias` — e logo
 * abaixo dele o `QuadroEmbutido` de Manutenções, com as abas do
 * `Sistema de Manutenções - Villa Prado.html`, cada uma terminando no rodapé.
 *
 * Como usar: a página só escolhe a casa, lê as duas listas e decide a situação
 * de cada uma; filtro, ordenação e formulário são de cada aba, e sobrevivem à
 * troca de aba porque o quadro mantém as abas montadas. Uma aba nova entra em
 * `ABAS` e em `paineis`, com a mesma `chave` nos dois. Quem só tem `ver` em
 * `manutencoes-boa-vista` recebe `podeEditar` falso, e as abas escondem o que
 * grava.
 *
 * A casa mostrada é, entre as de `casasDaFazenda` — as que ficam dentro da
 * Fazenda Boa Vista e a própria fazenda —, a primeira que tenha corretiva ou
 * preventiva registrada, e a primeira delas quando nenhuma tem registro. Uma
 * casa de fora da fazenda nunca é escolhida: sem casa da fazenda cadastrada, as
 * abas ficam vazias. Quando `useResidencias` falha — quem não tem `ver` em
 * Cadastros-base recebe 403 dela —, vale a casa do primeiro registro, que é a
 * casa em que esta tela grava. As listas chegam às abas **recortadas por essa
 * casa**: sem o recorte, duas casas apareceriam misturadas enquanto o
 * formulário gravaria tudo numa delas. Uma segunda casa com manutenção fica
 * invisível; o que falta para ela é um seletor.
 *
 * Enquanto a lista de casas não responde, as abas mostram "Carregando…": antes
 * disso não se sabe de que casa são os registros, e a tabela diria por um
 * instante que a casa não tem nada.
 *
 * No papel do Dashboard, o cabeçalho do módulo some e o respiro do painel vai a
 * zero (l. 422–424): a folha de 210 mm da `AbaDashboard` começa na borda do
 * papel, que não tem margem. A barra do topo da aplicação, a barra de logotipos
 * e as abas já somem em qualquer impressão.
 */
import { acessoAlcanca } from '@concierge/shared';
import { IconCalendarCheck, IconLayoutDashboard, IconTool } from '@tabler/icons-react';

import { type AbaDoApp, QuadroEmbutido } from '@/shared/embutido';
import { useAcesso } from '@/shared/hooks';
import { CabecalhoDoModulo } from '@/shared/ui/cabecalho-do-modulo';
import { casasDaFazenda, useResidencias } from '@/modules/cadastros-base';
import { AbaCorretivas } from '../components/aba-corretivas';
import { AbaDashboard } from '../components/aba-dashboard';
import { AbaPreventivas } from '../components/aba-preventivas';
import { RodapeDoMiolo } from '../components/rodape-do-miolo';
import { useCorretivas, usePreventivas } from '../hooks/usar-manutencoes';
import { situacaoDaConsulta } from '../regras';

/** As abas do SM, na ordem e com os ícones da origem (l. 491–496). */
const ABAS: readonly AbaDoApp[] = [
  { chave: 'dashboard', rotulo: 'Dashboard', Icone: IconLayoutDashboard },
  { chave: 'corretivas', rotulo: 'Corretivas', Icone: IconTool },
  { chave: 'preventivas', rotulo: 'Preventivas', Icone: IconCalendarCheck },
];

/**
 * Página das manutenções.
 *
 * @returns O cabeçalho do módulo e o quadro com as abas.
 */
export function PaginaManutencoes() {
  const acesso = useAcesso();
  const podeEditar =
    acesso.data !== undefined && acessoAlcanca(acesso.data, 'manutencoes-boa-vista', 'editar');

  const consultaCasas = useResidencias();
  const consultaCorretivas = useCorretivas();
  const consultaPreventivas = usePreventivas();

  const corretivas = consultaCorretivas.data ?? [];
  const preventivas = consultaPreventivas.data ?? [];
  const casas = casasDaFazenda(consultaCasas.data ?? []);

  const casasComRegistro = new Set(
    [...corretivas, ...preventivas].map((registro) => registro.residencia.id),
  );
  const residenciaId =
    consultaCasas.error !== null
      ? (corretivas[0]?.residencia.id ?? preventivas[0]?.residencia.id ?? '')
      : ((casas.find((casa) => casasComRegistro.has(casa.id)) ?? casas[0])?.id ?? '');
  const esperandoCasas = consultaCasas.isPending;

  const corretivasDaCasa = corretivas.filter(
    (corretiva) => corretiva.residencia.id === residenciaId,
  );
  const situacaoDasCorretivas = situacaoDaConsulta(consultaCorretivas, esperandoCasas);
  const preventivasDaCasa = preventivas.filter(
    (preventiva) => preventiva.residencia.id === residenciaId,
  );
  const situacaoDasPreventivas = situacaoDaConsulta(consultaPreventivas, esperandoCasas);

  /** Relê as corretivas depois de uma falha. */
  function handleTentarCorretivas() {
    void consultaCorretivas.refetch();
  }

  /** Relê as preventivas depois de uma falha. */
  function handleTentarPreventivas() {
    void consultaPreventivas.refetch();
  }

  return (
    <div>
      <div className="print:[body[data-impressao=imprimindo-dashboard-de-manutencoes]_&]:hidden">
        <CabecalhoDoModulo
          titulo="Residências"
          subtitulo="FAZENDA BOA VISTA | MANUTENÇÕES"
          voltarPara="/residencias"
        />
      </div>

      <QuadroEmbutido
        className="print:[body[data-impressao=imprimindo-dashboard-de-manutencoes]_&_[role=tabpanel]]:p-0"
        tema="manutencoes"
        abas={ABAS}
        paineis={{
          dashboard: (
            <>
              <AbaDashboard
                corretivas={corretivasDaCasa}
                situacao={situacaoDasCorretivas}
                onTentarDeNovo={handleTentarCorretivas}
              />
              <RodapeDoMiolo />
            </>
          ),
          corretivas: (
            <>
              <AbaCorretivas
                corretivas={corretivasDaCasa}
                situacao={situacaoDasCorretivas}
                onTentarDeNovo={handleTentarCorretivas}
                residenciaId={residenciaId}
                podeEditar={podeEditar}
              />
              <RodapeDoMiolo />
            </>
          ),
          preventivas: (
            <>
              <AbaPreventivas
                preventivas={preventivasDaCasa}
                situacao={situacaoDasPreventivas}
                onTentarDeNovo={handleTentarPreventivas}
                residenciaId={residenciaId}
                podeEditar={podeEditar}
              />
              <RodapeDoMiolo />
            </>
          ),
        }}
      />
    </div>
  );
}
